import { useState, useEffect, useCallback, useRef } from 'react';
import { api, LitLegionTaskWithStatus } from '@/lib/api';

interface UseLitLegionTasksOptions {
  category?: string;
  status?: string;
}

interface UseLitLegionTasksReturn {
  tasks: LitLegionTaskWithStatus[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useLitLegionTasks(options: UseLitLegionTasksOptions = {}): UseLitLegionTasksReturn {
  const [tasks, setTasks] = useState<LitLegionTaskWithStatus[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchTasks = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result = await api.listLitLegionTasks({
          category: options.category || undefined,
          status: options.status || undefined,
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setTasks((prev) => [...prev, ...result.taskStatuses]);
        } else {
          setTasks(result.taskStatuses);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load tasks');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.category, options.status],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchTasks(0, false);
  }, [fetchTasks]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchTasks(offset + limit, true);
    }
  }, [fetchTasks, loading, offset, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    tasks,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
